import { FieldValues, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { RiEyeFill, RiEyeOffFill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { z } from "zod";
import { Loader } from "../shared";
import ErrorMessage from "../errors/ErrorMessage";
import { LoginFormSchema } from "zod.schema";
import AuthenticationService from "services/Authentication";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCountdownTimer } from "hooks/useCountdownTimer";
import usePasswordVisibility from "hooks/usePasswordVisibility";
import { useNavigate, useNavigation, useLocation } from "react-router-dom";
import { AppDispatch, RootState } from "store";
import { login, loginWithOtp, verifyOtpAndLogin } from "store/reducers/authentication";
import formatTime from "utils/formatTime";
import { NavLink } from "react-router-dom";
import { useEffect } from "react";
import Icon from "assets/icons";


export type FormData = z.infer<typeof LoginFormSchema>;

const authService = new AuthenticationService();

const LoginForm = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const emailFromParams = queryParams.get("emailid") || "";

  const {
    register,
    handleSubmit,
    setFocus,
    setValue,
    formState: { errors },
    trigger,
    watch,
    getValues,
  } = useForm<FormData>({
    resolver: zodResolver(LoginFormSchema),
    defaultValues: {
      strategy: "password",
    },
    mode: "onChange",
  });

  const strategy = watch("strategy");
  const { timeLeft, isTimerRunning, start } = useCountdownTimer();
  const { showPassword, togglePasswordVisibility } = usePasswordVisibility();
  const router = useNavigate();
  const { isLoading, error } = useSelector((state: RootState) => state.auth);

  const dispatch = useDispatch<AppDispatch>();

  const onSubmit = async (data: FieldValues) => {
    try {
      if (strategy === "password") {
        const response = await dispatch(
          login({ username: data.username, password: data.password })
        ).unwrap();

        if (response.user && response.token) {
          toast.success("Logged In :" + new Date().toDateString());
          router("/dashboard");
        }
      }

      if (strategy === "otp") {
        const response = await dispatch(
          verifyOtpAndLogin({ email: data.username, otp: data.password })
        ).unwrap();

        if (response.user && response.token) {
          toast.success("OTP Verified. Logged in!");
          router("/dashboard");
        }
      }
    } catch (error) {
      console.error("Login error:", error);
    }
  };


  async function sendOneTimePassword() {
    const email = getValues("username");
    const isValidEmail = await trigger("username");

    if (!(!isTimerRunning && isValidEmail)) return;

    try {
      await dispatch(loginWithOtp({ username: email })).unwrap();

      // Don't check response - loginWithOtp only sends OTP, doesn't return user/token

      let waitDuration = 30;
      start(waitDuration);
      toast.success(
        `An OTP has been sent to your email. Please use it to log in.`
      );
    } catch (error) {
      console.error("Error wile Sending OTP", error);
    }
  }

  useEffect(() => {
    if (emailFromParams) {
      setValue("username", emailFromParams);
    }
  }, [emailFromParams, setValue]);


  return (
    <>
      <div className="w-3/4 p-10 m-auto my-[10%]">
        {error && (
          <div className="border-red-700  border-[2px] font-bold text-xs bg-red-300 p-1 rounded text-red-700 my-2">
            Error : {error}
          </div>
        )}
        <div className="mb-6">
          <img width={130} src={Icon.secondLogo} alt="Icon Image" />
        </div>
        <h2 className="text-app-blue mb-5 text-xl font-bold font-lato tracking-normal">
          Login To Learner
        </h2>
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col items-start">
            <input
              className="h-10 w-full rounded-2xl border-app-blue text-base  outline-none border  px-6"
              type="text"
              id="username"
              placeholder="Enter Email ID"
              {...register("username")}
              disabled={!!emailFromParams}
            />
            {errors.username?.message && (
              <ErrorMessage message={errors.username.message}></ErrorMessage>
            )}
          </div>
          <div className="flex flex-row justify-start gap-4 items-start">
            <div>
              <input
                type="radio"
                id="password"
                value="password"
                {...register("strategy")}
              />
              <label
                htmlFor="password"
                className="text-xs text-app-blue font-bold ml-2"
              >
                With Password
              </label>
            </div>
            <div>
              <input
                type="radio"
                id="otp"
                value="otp"
                {...register("strategy")}
                onChange={async () => {
                  const isValid = await trigger("username");
                  if (!isValid) {
                    setFocus("username");
                    setValue("strategy", "password");
                    return;
                  }

                  await sendOneTimePassword();
                }}
              />
              <label
                htmlFor="otp"
                className="text-xs text-app-blue font-bold ml-2"
              >
                With OTP on Email
              </label>
            </div>
          </div>

          <div className="flex flex-col">
            <div className="relative">
              <input
                placeholder={
                  strategy === "password"
                    ? "Enter Password"
                    : "Enter OTP received on your registered email ID"
                }
                {...register("password")}
                className="h-10 w-full rounded-2xl border-app-blue text-base  outline-none border  px-6"
                type={showPassword ? "text" : "password"}
              />
              <span
                className="absolute right-2 top-[25%]"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? (
                  <RiEyeFill color="#D4D6DB" size={25} />
                ) : (
                  <RiEyeOffFill color="#D4D6DB" size={25} />
                )}
              </span>
            </div>

            {errors.password?.message && (
              <ErrorMessage message={errors.password.message}></ErrorMessage>
            )}
          </div>
          {strategy === "password" && (
            <div className="text-start text-lge desktop:text-sm largescreen:text-lg underline">
              <NavLink
                className="font-semibold text-[#545455] ml-1"
                to={"/forgot-password"}
              >
                Forgot your Password?
              </NavLink>
            </div>
          )}
          {strategy === "otp" && (
            <div className="text-start text-base desktop:text-sm largescreen:text-xl underline font-semibold text-app-blue ml-1">
              <button
                disabled={isTimerRunning}
                className={`font-normal ${isTimerRunning
                  ? "text-app-gray"
                  : "text-app-blue hover:underline"
                  }`}
                onClick={() => sendOneTimePassword()}
              >
                Resend OTP{" "}
                {isTimerRunning && `in ${formatTime(timeLeft)} seconds`}
              </button>
            </div>
          )}
          <div className="">
            <button
              className="flex flex-row bg-gradient-to-r shadow text-white text-lg enabled:hover:scale-105 enabled:from-app-blue enabled:to-app-purple w-3/4 py-2 rounded-full desktop:text-lg largescreen:text-3xl disabled:bg-gray-500 disabled:cursor-wait"
              type="submit"
              disabled={isLoading}
            >
              <span className="flex mx-auto my-auto">
                Login
                {isLoading && <Loader size={30} />}
              </span>
            </button>
          </div>
          <p className="mt-2 text-[#5F488A]">
            Don’t have an account with us?{" "}
            <span
              className="underline cursor-pointer hover:text-[#4B356C] font-bold"
              onClick={() => router("/signup")}
            >
              Sign Up
            </span>
          </p>

        </form>
      </div>
    </>
  );
};

export default LoginForm;
